import type { FormEvent } from "react";
import React, { useContext, useEffect, useMemo, useState } from "react";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { FaChild, FaEdit, FaEnvelope, FaMoneyBillWave, FaPhone, FaSave, FaTimes, FaUserFriends } from "react-icons/fa";
import { AuthContext } from "../../../AuthProvider";
import PageTemplate from "../../../components/PageTemplate";
import SlidePageTransition from "../../../components/transitions/SlidePageTransition";
import ErrorPage from "../../../components/ErrorPage";
import Spinner from "../../../components/Spinner";
import Modal from "../../../components/Modal";
import BackButton from "../../../components/navigation/BackButton";
import { useCan } from "../../../authz/useCan";
import { calculateAge } from "../../../utils/dateUtils";
import { maskIban } from "../../../utils/ibanUtils";
import useFormatCurrency from "../../../utils/useFormatCurrency";
import { familySituationLabel, hasSeparateHouseholds } from "../../../utils/familySituation";
import { approveBankAccount, createBankAccount } from "../api";
import type { BankAccountFormValues, EntityId, Family, FamilyChild, FamilyDetailsTab, ParentSlot } from "../types";
import { childDetailsPath, familyEditPath, familyOverviewPath, navigateFamilyFlow, parentDetailsPath } from "../navigation";
import { useFamilyDetails } from "../hooks/useFamilyDetails";
import { isDeceased, isDeleted, isInactive, isWithdrawn, sortFamilyChildren } from "../utils/familyDetails";
import { FamilyHeader, FamilyStatCard, FamilyTabs } from "./components";

const parentSlots: ParentSlot[] = ["parent1", "parent2"];

const parentFullName = (family: Family, slot: ParentSlot): string =>
  [family[`${slot}FirstName`], family[`${slot}LastName`]].filter(Boolean).join(" ").trim();

const parentIdForSlot = (family: Family, slot: ParentSlot): EntityId | null =>
  family[`${slot}Id`] ?? family[`${slot}Uuid`] ?? null;

const parentEmailForSlot = (family: Family, slot: ParentSlot): string =>
  String(family[`${slot}Email`] ?? "").trim();

const parentPhoneForSlot = (family: Family, slot: ParentSlot): string =>
  String(family[`${slot}Phone`] ?? "").trim();

const emptyBankForms = (): Record<ParentSlot, BankAccountFormValues> => ({
  parent1: { iban: "", accountHolderName: "" },
  parent2: { iban: "", accountHolderName: "" },
});

const bankForSlot = (family: Family, slot: ParentSlot) => family[`${slot}BankAccount`] ?? null;

const FamilyDetails = () => {
  const { id = "" } = useParams();
  const { keycloak } = useContext(AuthContext);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const formatCurrency = useFormatCurrency();
  const canEditFamilyLegacy = useCan("families", "crud");
  const canEditFamilyDirect = useCan("families", "crud");
  const canEditFamilyManage = useCan("families", "manage");
  const canEditFamily = canEditFamilyLegacy || canEditFamilyDirect || canEditFamilyManage;
  const { family, currentYearFee, loading, showInitialSpinner, error, reload, setError } = useFamilyDetails(id, t);

  const [hideInactive, setHideInactive] = useState(false);
  const [activeTab, setActiveTab] = useState<FamilyDetailsTab>(() =>
    searchParams.get("tab") === "finance" ? "finance" : "info",
  );
  const [showForm, setShowForm] = useState<[boolean, boolean]>([false, false]);
  // One form per parent: both parents can have their own bank account.
  const [formData, setFormData] = useState<Record<ParentSlot, BankAccountFormValues>>(emptyBankForms);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  useEffect(() => {
    setActiveTab(searchParams.get("tab") === "finance" ? "finance" : "info");
  }, [searchParams]);

  const selectTab = (tab: FamilyDetailsTab) => {
    setActiveTab(tab);
    const next = new URLSearchParams(searchParams);
    if (tab === "info") next.delete("tab");
    else next.set("tab", tab);
    setSearchParams(next, { replace: true, state: location.state });
  };

  const openRelatedDetails = (path: string) =>
    navigate(path, {
      state: { returnTo: `/family/${id}/details?tab=${activeTab}` },
    });

  const childrenList = useMemo<FamilyChild[]>(
    () => (Array.isArray(family?.children) ? [...family.children].sort(sortFamilyChildren) : []),
    [family?.children]
  );
  const activeChildrenCount = childrenList.filter((child) => !isInactive(child)).length;
  // Deleted children are shown (greyed out) until Noctis removes them, but no longer count for the fee.
  const payingChildren = childrenList.filter((child) => !isDeleted(child));
  const filteredChildren = hideInactive ? childrenList.filter((child) => !isInactive(child)) : childrenList;
  const connectedParentsCount = family ? parentSlots.filter((slot) => parentIdForSlot(family, slot)).length : 0;

  const toggleForm = (index: number) => {
    // Closing a form discards what was typed in it.
    if (showForm[index]) {
      const slot = parentSlots[index];
      setFormData((current) => ({ ...current, [slot]: { iban: "", accountHolderName: "" } }));
    }
    setShowForm((current) => {
      const next: [boolean, boolean] = [...current] as [boolean, boolean];
      next[index] = !next[index];
      return next;
    });
  };

  const updateForm = (slot: ParentSlot, field: keyof BankAccountFormValues, value: string) => {
    setFormData((current) => ({ ...current, [slot]: { ...current[slot], [field]: value } }));
  };

  const handleApprove = async (bankAccountId: EntityId) => {
    try {
      await approveBankAccount(bankAccountId, keycloak?.tokenParsed?.sub);
      await reload();
    } catch (approveError) {
      setError(approveError as { message: string; status?: number });
    }
  };

  const handleCreateAccount = async (event: FormEvent<HTMLFormElement>, parentId: EntityId, slot: ParentSlot, index: number) => {
    event.preventDefault();
    try {
      await createBankAccount(parentId, formData[slot]);
      setFormData((current) => ({ ...current, [slot]: { iban: "", accountHolderName: "" } }));
      setShowForm((current) => {
        const next: [boolean, boolean] = [...current] as [boolean, boolean];
        next[index] = false;
        return next;
      });
      await reload();
      setShowSuccessModal(true);
    } catch (createError) {
      setError(createError as { message: string; status?: number });
    }
  };

  if (loading) return showInitialSpinner ? <Spinner /> : null;
  if (error) return <ErrorPage message={error.message} status={error.status} onRetry={() => window.location.reload()} />;
  if (!family) return <ErrorPage message={t("family.details.not_found", { defaultValue: "Family not found." })} status={undefined} onRetry={() => window.location.reload()} />;

  return (
    <PageTemplate title={null} wrapperClassName="max-w-7xl mx-auto">
      <SlidePageTransition className="mx-auto rounded-[30px] border border-slate-200/80 bg-white/95 p-5 text-slate-950 shadow-[0_28px_90px_rgba(15,23,42,0.12)] backdrop-blur dark:border-white/10 dark:bg-slate-950/80 dark:text-white sm:p-6">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <BackButton fallback={familyOverviewPath} />
          {canEditFamily && (
            <button
              type="button"
              onClick={() => navigateFamilyFlow(navigate, familyEditPath(id), "forward")}
              className="inline-flex items-center justify-center gap-2 rounded-full border border-slate-200 bg-white px-5 py-2.5 text-sm font-semibold text-slate-700 shadow-sm transition hover:-translate-y-0.5 hover:border-indigo-200 hover:text-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 motion-reduce:hover:translate-y-0 dark:border-white/10 dark:bg-white/10 dark:text-slate-100 dark:hover:text-indigo-200"
            >
              <FaEdit aria-hidden="true" />
              {t("common.actions.edit", { defaultValue: "Edit" })}
            </button>
          )}
        </div>

        <div className="flex flex-col gap-6">
          <FamilyHeader family={family} childrenCount={childrenList.length} connectedParentsCount={connectedParentsCount} t={t} />

          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <FamilyStatCard
              icon={FaChild}
              label={t("family.details.children", { defaultValue: "Children" })}
              value={childrenList.length}
              helper={
                activeChildrenCount > 0
                  ? t("family.details.helper_active_children", { count: activeChildrenCount, defaultValue: "{{count}} active" })
                  : t("family.details.helper_no_active_children", { defaultValue: "No active children" })
              }
              tone="indigo"
            />
            <FamilyStatCard
              icon={FaUserFriends}
              label={t("family.details.parents", { defaultValue: "Parents" })}
              value={connectedParentsCount}
              helper={t("family.details.helper_parents_connected", { count: connectedParentsCount, defaultValue: "Linked to this family" })}
              tone="sky"
            />
            <FamilyStatCard
              icon={FaUserFriends}
              label={t("family.details.marital_status", { defaultValue: "Marital status" })}
              value={familySituationLabel(t, family.familySituation)}
              helper={
                hasSeparateHouseholds(family.familySituation)
                  ? t("family.details.separate_household_helper", { defaultValue: "Separated household" })
                  : t("family.details.united_helper", { defaultValue: "Single household" })
              }
              tone={hasSeparateHouseholds(family.familySituation) ? "amber" : "emerald"}
            />
            {currentYearFee != null && (
              <FamilyStatCard
                icon={FaMoneyBillWave}
                label={t("finance.default_monthly_fee", { defaultValue: "Default monthly fee" })}
                value={formatCurrency(currentYearFee)}
                helper={t("finance.current_year", { defaultValue: "Current school year" })}
                tone="emerald"
              />
            )}
          </div>

          <section className="rounded-[24px] border border-slate-200/80 bg-white p-4 shadow-sm dark:border-white/10 dark:bg-white/5 sm:p-5">
            <FamilyTabs activeTab={activeTab} onChange={selectTab} t={t} />

            {activeTab === "info" && (
              <div className="mt-6 space-y-6">
                <section className="rounded-[18px] border border-sky-200/80 bg-sky-50/60 p-5 shadow-sm dark:border-sky-500/40 dark:bg-sky-950/25">
                  <h2 className="text-lg font-bold text-slate-950 dark:text-white">
                    {t("family.details.parents", { defaultValue: "Parents" })}
                  </h2>
                  <div className="mt-4 grid gap-4 sm:grid-cols-2">
                    {parentSlots.map((slot, index) => {
                      const parentId = parentIdForSlot(family, slot);
                      const name = parentFullName(family, slot);
                      const email = parentEmailForSlot(family, slot);
                      const phone = parentPhoneForSlot(family, slot);
                      if (!parentId || !name) return null;
                      return (
                        <button
                          type="button"
                          key={slot}
                          onClick={() => openRelatedDetails(parentDetailsPath(parentId))}
                          className="flex flex-col rounded-2xl border border-white/70 bg-white/95 px-4 py-3 text-left shadow-sm transition hover:border-indigo-200 hover:text-indigo-700 dark:border-white/10 dark:bg-white/10 dark:text-slate-100 dark:hover:border-indigo-500/40"
                        >
                          <span className="text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                            {t("family.details.parent_label", { defaultValue: "Parent" })} {index + 1}
                          </span>
                          <span className="mt-1 text-lg font-bold">{name}</span>
                          <span className="mt-3 flex flex-col gap-2 text-sm font-semibold text-slate-600 dark:text-slate-300">
                            <span className="inline-flex min-w-0 items-center gap-2">
                              <FaEnvelope aria-hidden="true" className="shrink-0 text-sky-500" />
                              <span className="truncate">{email || t("common.not_available", { defaultValue: "Not available" })}</span>
                            </span>
                            <span className="inline-flex min-w-0 items-center gap-2">
                              <FaPhone aria-hidden="true" className="shrink-0 text-emerald-500" />
                              <span className="truncate">{phone || t("common.not_available", { defaultValue: "Not available" })}</span>
                            </span>
                          </span>
                          <span className="mt-2 text-sm font-semibold text-indigo-600 dark:text-indigo-300">
                            {t("family.details.open_parent_profile", { defaultValue: "Open parent profile" })}
                          </span>
                        </button>
                      );
                    })}
                    {connectedParentsCount === 0 && (
                      <p className="rounded-2xl border border-red-200 bg-red-50/80 p-4 text-sm font-bold text-red-700 shadow-sm dark:border-red-700/50 dark:bg-red-900/30 dark:text-red-200">
                        {t("family.details.no_parents", { defaultValue: "No parents linked" })}
                      </p>
                    )}
                  </div>
                </section>

                <section className="rounded-[18px] border border-indigo-200/80 bg-indigo-50/55 p-5 shadow-sm dark:border-indigo-500/40 dark:bg-indigo-950/25">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <h2 className="text-lg font-bold text-slate-950 dark:text-white">
                      {t("family.details.children", { defaultValue: "Children" })}
                    </h2>
                    <label className="inline-flex items-center gap-2 text-xs font-bold text-slate-600 dark:text-slate-300">
                      <input
                        type="checkbox"
                        checked={hideInactive}
                        onChange={(event) => setHideInactive(event.target.checked)}
                        className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                      />
                      {t("family.details.children_inactive_hide", { defaultValue: "Hide inactive children" })}
                    </label>
                  </div>
                  <div className="mt-4 space-y-3">
                    {filteredChildren.length > 0 ? (
                      filteredChildren.map((child) => (
                        <FamilyChildRow key={String(child.id)} child={child} onOpen={() => openRelatedDetails(childDetailsPath(child.id))} t={t} />
                      ))
                    ) : (
                      <p className="text-sm text-slate-500 dark:text-slate-400">
                        {t("family.parents.details.no_children_assigned", { defaultValue: "No children assigned" })}
                      </p>
                    )}
                  </div>
                </section>
              </div>
            )}

            {activeTab === "finance" && (
              <div className="mt-6 grid gap-6 lg:grid-cols-3">
                <div className="space-y-4 lg:col-span-2">
                  {parentSlots.map((slot, index) => {
                    const parentId = parentIdForSlot(family, slot);
                    const name = parentFullName(family, slot);
                    const bank = bankForSlot(family, slot);
                    if (!parentId || !name) return null;
                    return (
                      <div key={slot} className="rounded-[18px] border border-slate-200/80 bg-white p-5 shadow-sm dark:border-white/10 dark:bg-white/10">
                        <div className="flex flex-wrap items-center justify-between gap-3">
                          <div>
                            <p className="text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                              {t("family.details.parent_label", { defaultValue: "Parent" })} {index + 1}
                            </p>
                            <p className="text-lg font-bold text-slate-950 dark:text-white">{name}</p>
                          </div>
                          <button
                            type="button"
                            onClick={() => openRelatedDetails(parentDetailsPath(parentId))}
                            className="inline-flex items-center rounded-full border border-indigo-200 bg-indigo-50 px-3 py-1 text-xs font-bold text-indigo-700 transition hover:bg-indigo-100 dark:border-indigo-400/25 dark:bg-indigo-400/10 dark:text-indigo-200"
                          >
                            {t("common.actions.view", { defaultValue: "View" })}
                          </button>
                        </div>
                        {!bank ? (
                          <div className="mt-4 rounded-2xl border border-dashed border-slate-300 bg-slate-50/70 p-4 text-sm text-slate-600 dark:border-white/10 dark:bg-white/5 dark:text-slate-300">
                            <p>{t("finance.no_account", { defaultValue: "No bank account" })}</p>
                            {!showForm[index] ? (
                              <button
                                type="button"
                                className="mt-3 inline-flex items-center rounded-full border border-sky-200 bg-sky-50 px-4 py-2 text-sm font-bold text-sky-700 transition hover:bg-sky-100 dark:border-sky-400/25 dark:bg-sky-400/10 dark:text-sky-200"
                                onClick={() => toggleForm(index)}
                              >
                                {t("finance.add_account", { defaultValue: "Add account" })}
                              </button>
                            ) : (
                              <form onSubmit={(event) => handleCreateAccount(event, parentId, slot, index)} className="mt-3 space-y-3">
                                <input
                                  type="text"
                                  placeholder={t("finance.iban", { defaultValue: "IBAN" })}
                                  value={formData[slot].iban}
                                  onChange={(event) => updateForm(slot, "iban", event.target.value)}
                                  className="h-12 w-full rounded-xl border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-950 shadow-sm outline-none transition focus:border-indigo-300 focus:ring-2 focus:ring-indigo-200 dark:border-white/10 dark:bg-slate-950/60 dark:text-white"
                                />
                                <input
                                  type="text"
                                  placeholder={t("finance.account_holder", { defaultValue: "Account holder" })}
                                  value={formData[slot].accountHolderName}
                                  onChange={(event) => updateForm(slot, "accountHolderName", event.target.value)}
                                  className="h-12 w-full rounded-xl border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-950 shadow-sm outline-none transition focus:border-indigo-300 focus:ring-2 focus:ring-indigo-200 dark:border-white/10 dark:bg-slate-950/60 dark:text-white"
                                />
                                <div className="flex flex-wrap gap-3">
                                  <button type="submit" className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-4 py-2 text-sm font-bold text-emerald-700 transition hover:bg-emerald-100 dark:border-emerald-400/25 dark:bg-emerald-400/10 dark:text-emerald-200">
                                    <FaSave aria-hidden="true" />
                                    {t("common.save", { defaultValue: "Save" })}
                                  </button>
                                  <button type="button" onClick={() => toggleForm(index)} className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-700 transition hover:border-indigo-200 hover:text-indigo-700 dark:border-white/10 dark:bg-white/10 dark:text-slate-100">
                                    <FaTimes aria-hidden="true" />
                                    {t("common.cancel", { defaultValue: "Cancel" })}
                                  </button>
                                </div>
                              </form>
                            )}
                          </div>
                        ) : (
                          <div className="mt-4 space-y-2 text-sm text-slate-800 dark:text-slate-200">
                            <p>
                              <strong>{t("finance.iban", { defaultValue: "IBAN" })}:</strong> {maskIban(bank.iban || "")}
                            </p>
                            <p>
                              <strong>{t("finance.account_holder", { defaultValue: "Account holder" })}:</strong> {bank.accountHolderName || "-"}
                            </p>
                            {bank.proposedIban && (
                              <div className="mt-3 rounded-2xl border border-amber-200 bg-amber-50 p-3 text-amber-800 dark:border-amber-400/30 dark:bg-amber-400/10 dark:text-amber-100">
                                <p>
                                  <strong>{t("finance.proposed_iban", { defaultValue: "Proposed IBAN" })}:</strong> {maskIban(bank.proposedIban)}
                                </p>
                                <p>
                                  <strong>{t("finance.proposed_account_holder", { defaultValue: "Proposed account holder" })}:</strong> {bank.proposedAccountHolderName || "-"}
                                </p>
                                <button
                                  type="button"
                                  className="mt-2 inline-flex items-center rounded-full border border-emerald-200 bg-emerald-50 px-4 py-2 text-xs font-bold text-emerald-700 transition hover:bg-emerald-100 dark:border-emerald-400/25 dark:bg-emerald-400/10 dark:text-emerald-200"
                                  onClick={() => handleApprove(bank.id)}
                                >
                                  {t("finance.approve", { defaultValue: "Approve" })}
                                </button>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
                <section className="rounded-[18px] border border-emerald-200/80 bg-emerald-50/55 p-5 shadow-sm dark:border-emerald-500/40 dark:bg-emerald-950/25">
                  <h2 className="text-lg font-bold text-slate-950 dark:text-white">
                    {t("finance.monthly_overview", { defaultValue: "Monthly overview" })}
                  </h2>
                  <p className="text-xs font-semibold text-slate-500 dark:text-slate-400">
                    {t("finance.current_year", { defaultValue: "Current school year" })}
                  </p>
                  {payingChildren.length > 0 && currentYearFee != null ? (
                    <div className="mt-4 space-y-2 text-sm text-slate-900 dark:text-slate-100">
                      {payingChildren.map((child) => (
                        <div key={String(child.id)} className="flex items-center justify-between border-b border-emerald-100 pb-2 dark:border-emerald-400/20">
                          <span>{child.firstName || "-"}</span>
                          <span>{formatCurrency(Number(child.monthlyContribution ?? currentYearFee))}</span>
                        </div>
                      ))}
                      <div className="flex items-center justify-between pt-2 text-base font-bold">
                        <span>{t("finance.total", { defaultValue: "Total" })}</span>
                        <span>{formatCurrency(payingChildren.reduce((sum, child) => sum + Number(child.monthlyContribution ?? currentYearFee), 0))}</span>
                      </div>
                    </div>
                  ) : (
                    <p className="mt-4 text-sm italic text-amber-800 dark:text-amber-100">
                      {t("finance.no_fee_found", { defaultValue: "No fee found" })}
                    </p>
                  )}
                </section>
              </div>
            )}
          </section>
        </div>
      </SlidePageTransition>

      <Modal
        show={showSuccessModal}
        type="success"
        title={t("finance.added_title", { defaultValue: "Bank account added" })}
        message={t("finance.add_success", { defaultValue: "The bank account was successfully added." })}
        onClose={() => setShowSuccessModal(false)}
      />
    </PageTemplate>
  );
};

interface FamilyChildRowProps {
  child: FamilyChild;
  onOpen: () => void;
  t: TFunction;
}

const FamilyChildRow: React.FC<FamilyChildRowProps> = ({ child, onOpen, t }) => {
  const deceased = isDeceased(child);
  const withdrawn = isWithdrawn(child);
  const deleted = isDeleted(child);
  const title = deleted
    ? t("family.details.child_deleted_title", { defaultValue: "Deleted; the child is removed for good once the retention period has passed" })
    : deceased
    ? t("family.details.child_deceased_title", { date: child.deceasedAt, defaultValue: "Deceased on {{date}}" })
    : withdrawn
      ? t("family.details.child_withdrawn_title", { date: child.withdrawnAt, defaultValue: "Withdrawn on {{date}}" })
      : undefined;
  const content = (
    <>
      <div>
        <p className={`text-base font-bold ${deceased || deleted ? "text-slate-400 line-through" : "text-slate-950 dark:text-white"}`}>
          {[child.firstName, child.lastName].filter(Boolean).join(" ") || "-"}
        </p>
        <p className="text-xs text-slate-500 dark:text-slate-400">
          {t("family.details.age", { defaultValue: "Age" })}: {calculateAge(child.dateBirth)}
        </p>
      </div>
      {deceased && (
        <span className="inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-bold text-slate-600 dark:border-white/10 dark:bg-white/5 dark:text-slate-200">
          {t("family.details.child_deceased", { defaultValue: "Deceased" })}
        </span>
      )}
      {deleted && (
        <span className="inline-flex items-center rounded-full border border-rose-200 bg-rose-50 px-3 py-1 text-xs font-bold text-rose-700 dark:border-rose-400/30 dark:bg-rose-400/10 dark:text-rose-200">
          {t("family.details.child_deleted", { defaultValue: "Deleted" })}
        </span>
      )}
      {withdrawn && !deceased && !deleted && (
        <span className="inline-flex items-center rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-xs font-bold text-amber-700 dark:border-amber-400/30 dark:bg-amber-400/10 dark:text-amber-100">
          {t("family.details.child_withdrawn", { defaultValue: "Withdrawn" })}
        </span>
      )}
    </>
  );

  const className = "flex w-full items-center justify-between gap-3 rounded-xl border border-white/70 bg-white/95 px-4 py-3 text-left shadow-sm transition dark:border-white/10 dark:bg-white/10";
  return deceased ? (
    <div className={`${className} cursor-not-allowed opacity-70`} title={title}>
      {content}
    </div>
  ) : (
    <button type="button" onClick={onOpen} data-deleted={deleted ? "true" : undefined}
      className={`${className} ${deleted ? "opacity-60" : ""} hover:border-indigo-200 hover:text-indigo-700 dark:hover:border-indigo-500/40 dark:hover:text-indigo-200`} title={title}>
      {content}
    </button>
  );
};

export default FamilyDetails;
